"use client";

import type { ReactNode } from "react";
import { useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { format } from "date-fns";
import type { EconomicEvent } from "@/vybe-trader-client/economic-calendar/types";
import type { ImpactLevel } from "./impact-styles";
import {
  impactBadgeClassName,
  impactLucideIcons,
  sentimentVsForecast,
  sentimentIconClass,
  sentimentTextClass,
} from "./impact-styles";
import { stableDayKeyFromEvent } from "./selectors";
import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { currencyPairLabel } from "./currency-flag-emoji";

const ROW_H = 28;
/** Date | Time | Impact | CCY | Event | Prev | Fcst | Act */
const GRID_COLS =
  "56px 58px 68px 76px minmax(0,1fr) 56px 56px 56px";

interface EconomicCalendarScheduleTableProps {
  events: EconomicEvent[];
  /** Row activation (keyboard + click) when provided */
  onRowClick?: (event: EconomicEvent) => void;
}

function formatEventDateShort(event: EconomicEvent): string {
  const key = stableDayKeyFromEvent(event);
  const d = new Date(key + "T12:00:00");
  if (Number.isNaN(d.getTime())) {
    return key.slice(5);
  }
  return format(d, "MMM d");
}

export function EconomicCalendarScheduleTable({
  events,
  onRowClick,
}: EconomicCalendarScheduleTableProps) {
  const sorted = useMemo(
    () =>
      [...events].sort(
        (a, b) =>
          new Date(a.dateTimeISO).getTime() -
          new Date(b.dateTimeISO).getTime()
      ),
    [events]
  );

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_H,
    overscan: 16,
  });

  const vItems = virtualizer.getVirtualItems();

  const interactive = !!onRowClick;

  const scheduleHeaderGrid = (
    <div
      className="grid shrink-0 border-b border-border bg-muted/30 text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
      style={{ gridTemplateColumns: GRID_COLS }}
    >
      <HeaderCell>Date</HeaderCell>
      <HeaderCell>Time</HeaderCell>
      <HeaderCell>Impact</HeaderCell>
      <HeaderCell align="center">Ccy</HeaderCell>
      <HeaderCell>Event</HeaderCell>
      <HeaderCell>Prev</HeaderCell>
      <HeaderCell>Fcst</HeaderCell>
      <HeaderCell>Act</HeaderCell>
    </div>
  );

  const scheduleBody =
    sorted.length === 0 ? (
      <div className="px-2 py-6 text-center text-[11px] text-muted-foreground">
        No events for this day.
      </div>
    ) : (
      <div ref={parentRef} className="min-h-0 flex-1 overflow-auto">
        <div
          style={{
            height: virtualizer.getTotalSize(),
            position: "relative",
          }}
        >
          {vItems.map((vi) => {
            const e = sorted[vi.index];
            return (
              <div
                key={`${e.dateTimeISO}-${e.name}-${vi.index}`}
                className="absolute left-0 right-0 border-b border-border"
                style={{
                  transform: `translateY(${vi.start}px)`,
                  height: `${ROW_H}px`,
                }}
              >
                <TableGridRow
                  event={e}
                  interactive={interactive}
                  onActivate={() => onRowClick?.(e)}
                />
              </div>
            );
          })}
        </div>
      </div>
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border">
      {scheduleHeaderGrid}
      {scheduleBody}
    </div>
  );
}

function HeaderCell({
  children,
  align,
}: {
  children: ReactNode;
  align?: "center";
}) {
  return (
    <div
      className={cn(
        "truncate px-1 py-1",
        align === "center" && "text-center"
      )}
    >
      {children}
    </div>
  );
}

function TableGridRow({
  event,
  interactive,
  onActivate,
}: {
  event: EconomicEvent;
  interactive: boolean;
  onActivate: () => void;
}) {
  const pair = currencyPairLabel(event.currency);
  const imp = event.impact as ImpactLevel;
  const Icon = impactLucideIcons[imp];
  const tone = sentimentVsForecast(event.actual ?? "", event.forecast ?? "");

  const icon =
    tone === "up" ? (
      <TrendingUp className={`h-2.5 w-2.5 shrink-0 ${sentimentIconClass("up")}`} />
    ) : tone === "down" ? (
      <TrendingDown
        className={`h-2.5 w-2.5 shrink-0 ${sentimentIconClass("down")}`}
      />
    ) : (
      <Minus className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
    );

  const time =
    event.timeLabel?.trim() ||
    (() => {
      try {
        return format(new Date(event.dateTimeISO), "HH:mm");
      } catch {
        return "—";
      }
    })();

  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onActivate : undefined}
      onKeyDown={(ev) => {
        if (!interactive) return;
        if (ev.key === "Enter" || ev.key === " ") {
          ev.preventDefault();
          onActivate();
        }
      }}
      className={cn(
        "grid h-full items-center gap-px text-[10px] leading-tight hover:bg-muted/30",
        interactive && "cursor-pointer focus-visible:bg-muted/30 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      )}
      style={{ gridTemplateColumns: GRID_COLS }}
    >
      <MonoCell muted>{formatEventDateShort(event)}</MonoCell>
      <MonoCell muted>{time}</MonoCell>
      <div className="flex min-w-0 justify-start overflow-hidden px-1">
        <span
          className={cn(
            "inline-flex max-w-full items-center gap-0.5 truncate rounded border px-1 py-px text-[9px]",
            impactBadgeClassName(imp)
          )}
        >
          <Icon className="h-2.5 w-2.5 shrink-0" />
          <span className="truncate">{imp}</span>
        </span>
      </div>
      <div
        className="flex items-center justify-center gap-1 px-1 font-mono text-[10px] text-foreground"
        title={`${pair.codeUpper}`}
      >
        <span
          className="select-none text-[13px] leading-none"
          aria-hidden
        >
          {pair.flag}
        </span>
        <span>{pair.codeUpper}</span>
      </div>
      <div
        className="min-w-0 truncate px-1 font-medium text-foreground"
        title={event.name}
      >
        {event.name}
      </div>
      <MonoCell>{event.previous || "–"}</MonoCell>
      <MonoCell>{event.forecast || "–"}</MonoCell>
      <div className="flex min-w-0 items-center gap-0.5 px-1">
        <span
          className={cn(
            "inline-flex min-w-0 items-center gap-0.5 truncate font-mono",
            sentimentTextClass(tone)
          )}
        >
          {event.actual?.trim() ? icon : null}
          {event.actual?.trim() || "–"}
        </span>
      </div>
    </div>
  );
}

function MonoCell({
  children,
  muted,
}: {
  children: ReactNode;
  muted?: boolean;
}) {
  return (
    <div
      className={cn(
        "truncate px-1 font-mono tabular-nums",
        muted ? "text-muted-foreground" : "text-foreground"
      )}
    >
      {children}
    </div>
  );
}
